/**
 * useIsMobile — fonte única da verdade para detecção de viewport mobile.
 *
 * Retorna `true` quando a largura da janela é menor que o breakpoint (768px por
 * padrão). Usa matchMedia (reage a rotação/redimensionamento) e é seguro para SSR.
 *
 * IMPORTANTE: este hook só liga o "caminho mobile" da UI. Acima do breakpoint o
 * app continua renderizando exatamente o layout web atual — nada do desktop muda.
 */
import { useState, useEffect } from 'react';

export const MOBILE_BREAKPOINT = 768;

const useIsMobile = (breakpoint = MOBILE_BREAKPOINT) => {
  const query = `(max-width: ${breakpoint - 0.02}px)`;

  const getMatch = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  };

  const [isMobile, setIsMobile] = useState(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mql = window.matchMedia(query);
    const handler = (event) => setIsMobile(event.matches);

    // Sincroniza imediatamente (caso o valor tenha mudado entre render e efeito)
    setIsMobile(mql.matches);

    // addEventListener é o padrão moderno; addListener cobre Safari < 14.
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
    } else {
      mql.addListener(handler);
    }

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', handler);
      } else {
        mql.removeListener(handler);
      }
    };
  }, [query]);

  return isMobile;
};

export default useIsMobile;
